import { ArrowLeft, KeyRound, Mail, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { SignInForm } from "../components/SignInForm.js";
import { LinkAction, Screen } from "../components/Screen.js";
import { useSignIn } from "../hooks/useSignIn.js";
import { previewParty } from "../services/partyService.js";

/**
 * Sign in with a password.
 *
 * # One screen, one form
 *
 * The emailed-link flow briefly lived here too, under this form, and it was
 * wrong on sight: three inputs on one screen, two of them asking for an email
 * address, and no way to tell which one you were supposed to fill in. Two ways
 * of signing in are two screens.
 *
 * It lives at `/signin` — a route that already existed for the other end of the
 * link and already rendered exactly this form when it had no token to spend.
 * See `SignInLinkPage`.
 *
 * # What this screen may not say
 *
 * ADR 0010: the response says nothing an anonymous caller should not learn. A
 * wrong address and a wrong password come back as **one** code
 * (`invalid_credentials`) precisely so that this form cannot be used to find out
 * which addresses have accounts. That mapping lives in `hooks/useSignIn.ts` —
 * one copy, shared with the door, so a case added to it cannot apply to only
 * one of the two forms.
 *
 * # `?code=` — the party somebody was invited to
 *
 * Phase 20f-2. Since ADR 0027 a party link handed to somebody not signed in
 * cannot seat them, so `JoinPanel` sends them here carrying the code. This
 * screen names the party they are trying to reach and, on success, lands them in
 * it rather than on the front door — otherwise signing in *loses* the invitation
 * and they have to go back to a chat thread to find it again, mid-party.
 *
 * ⚠️ **The destination comes from the preview's own `code`, never from the query
 * string.** A code that resolves to nothing is not an error worth refusing a
 * sign-in over — somebody may simply want to sign in — so a bad code degrades to
 * the front door rather than to a party page that has to explain itself.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const invited = useInvite(params.get("code"));
  const { submit, busy, error } = useSignIn(invited ? `/party/${invited.code}` : "/");

  return (
    <Screen
      title={t("login.title")}
      lead={invited ? t("login.toJoin", { party: invited.name }) : t("login.lead")}
      footer={
        // Two rows of two, matching the door (phase 20a). The ways *in* on top
        // — an account you do not have, or a way in without a password — and
        // the two escapes underneath.
        <div className="grid w-full grid-cols-2 gap-2">
          <LinkAction to="/register" icon={UserPlus} accent>
            {t("login.createAccount")}
          </LinkAction>
          <LinkAction to="/signin" icon={Mail}>
            {t("home.door.useLink")}
          </LinkAction>
          <LinkAction to="/forgot" icon={KeyRound}>
            {t("login.forgot")}
          </LinkAction>
          <LinkAction to="/" icon={ArrowLeft}>
            {t("common.back")}
          </LinkAction>
        </div>
      }
    >
      <SignInForm onSubmit={submit} busy={busy} error={error} />
    </Screen>
  );
}

/** What is behind a `?code=`, once the server has been asked. */
interface Invite {
  code: string;
  name: string;
}

/**
 * Resolve a `?code=` to a party, or to `null`.
 *
 * `null` covers all three of "no code", "still asking" and "no such party",
 * because this screen does the same thing in all three: it is a sign-in form.
 * The party is what it can additionally *say*, not what it is for.
 *
 * ⚠️ **The answer is stamped with the code it was for**, and the effect never
 * clears state synchronously. Both fall out of the same rule (`eslint` enforces
 * the second): an effect that calls `setState` in its own body triggers a
 * cascading render, so "no code" has to be *derived* on the way out rather than
 * written on the way in — and once it is derived, a stale answer from a previous
 * code is a real case rather than a hypothetical one.
 */
function useInvite(code: string | null): Invite | null {
  const { t } = useTranslation();
  const [seen, setSeen] = useState<{
    for: string;
    code: string;
    name: string | null;
  } | null>(null);

  useEffect(() => {
    if (!code) {
      return;
    }
    let cancelled = false;
    void previewParty(code).then(
      (party) => {
        if (!cancelled && party) {
          setSeen({ for: code, code: party.code, name: party.name });
        }
      },
      () => {
        // A server that cannot be reached is not this screen's problem to
        // report: the sign-in below will say so in its own words if it fails.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code || seen?.for !== code) {
    return null;
  }
  return { code: seen.code, name: seen.name ?? t("login.theParty") };
}
